import type { Order as SupabaseOrder } from '@/lib/supabase';

type OrderStatus = SupabaseOrder['status'];

interface TimelineStep {
  key: OrderStatus | 'placed';
  label: string;
  sublabel: string;
  icon: string;
}

const STEPS: TimelineStep[] = [
  {
    key: 'placed',
    label: 'Order Placed',
    sublabel: 'Aapka order receive hua',
    icon: 'ri-shopping-bag-3-line',
  },
  {
    key: 'confirmed',
    label: 'Confirmed',
    sublabel: 'Store ne confirm kar diya',
    icon: 'ri-checkbox-circle-line',
  },
  {
    key: 'shipped',
    label: 'Out for Delivery',
    sublabel: 'Delivery boy raste mein hai',
    icon: 'ri-e-bike-2-fill',
  },
  {
    key: 'delivered',
    label: 'Delivered',
    sublabel: 'Order deliver ho gaya!',
    icon: 'ri-home-5-fill',
  },
];

// Map raw status to step index
function getStepIndex(status: OrderStatus): number {
  switch (status) {
    case 'pending': return 0;
    case 'confirmed': return 1;
    case 'shipped': return 2;
    case 'delivered': return 3;
    case 'cancelled': return -1;
    default: return 0;
  }
}

interface OrderTimelineProps {
  status: OrderStatus;
  createdAt: string;
  updatedAt: string;
  compact?: boolean;
}

export default function OrderTimeline({ status, createdAt, updatedAt, compact = false }: OrderTimelineProps) {
  const isCancelled = status === 'cancelled';
  const currentIdx = getStepIndex(status);

  if (isCancelled) {
    return (
      <div className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl bg-red-50 border border-red-200">
        <div className="w-7 h-7 rounded-full bg-red-500 flex items-center justify-center shrink-0">
          <i className="ri-close-line text-white text-sm"></i>
        </div>
        <div>
          <p className="text-red-700 font-bold text-xs">Order Cancelled</p>
          <p className="text-red-400 text-[10px]">
            {new Date(updatedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true })}
          </p>
        </div>
      </div>
    );
  }

  if (compact) {
    // Compact horizontal pill timeline
    return (
      <div className="flex items-center gap-0 w-full">
        {STEPS.map((step, idx) => {
          const isCompleted = idx < currentIdx;
          const isActive = idx === currentIdx;
          const isFuture = idx > currentIdx;
          return (
            <div key={step.key} className="flex items-center flex-1 min-w-0">
              {/* Step dot */}
              <div className="flex flex-col items-center shrink-0">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center transition-all ${
                  isCompleted
                    ? 'bg-green-500 text-white'
                    : isActive
                    ? 'bg-orange-500 text-white ring-2 ring-orange-200'
                    : 'bg-gray-100 text-gray-300'
                }`}>
                  {isCompleted ? (
                    <i className="ri-check-line text-[10px]"></i>
                  ) : (
                    <i className={`${step.icon} text-[10px]`}></i>
                  )}
                </div>
                <p className={`text-[8px] font-bold mt-0.5 text-center leading-tight whitespace-nowrap ${
                  isCompleted ? 'text-green-600' : isActive ? 'text-orange-600' : 'text-gray-300'
                }`}>
                  {step.label.split(' ')[0]}
                </p>
              </div>
              {/* Connector line */}
              {idx < STEPS.length - 1 && (
                <div className={`flex-1 h-0.5 mx-0.5 rounded-full ${
                  idx < currentIdx ? 'bg-green-400' : 'bg-gray-200'
                }`}></div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  // Full vertical timeline
  return (
    <div className="space-y-0">
      {STEPS.map((step, idx) => {
        const isCompleted = idx < currentIdx;
        const isActive = idx === currentIdx;
        const isFuture = idx > currentIdx;
        const isLast = idx === STEPS.length - 1;

        // Estimate timestamps
        const created = new Date(createdAt);
        const updated = new Date(updatedAt);
        let stepTime: Date | null = null;
        if (isCompleted || isActive) {
          if (idx === 0) stepTime = created;
          else if (isActive) stepTime = updated;
          else {
            // Interpolate between created and updated
            const fraction = idx / (currentIdx || 1);
            stepTime = new Date(created.getTime() + fraction * (updated.getTime() - created.getTime()));
          }
        }

        return (
          <div key={step.key} className="flex gap-3">
            {/* Left: icon + line */}
            <div className="flex flex-col items-center shrink-0">
              <div className={`w-9 h-9 rounded-full flex items-center justify-center transition-all duration-500 ${
                isCompleted
                  ? 'bg-green-500 text-white shadow-md shadow-green-200'
                  : isActive
                  ? 'bg-orange-500 text-white shadow-md shadow-orange-200 ring-4 ring-orange-100'
                  : 'bg-gray-100 text-gray-300'
              } ${isActive ? 'animate-pulse' : ''}`}>
                {isCompleted ? (
                  <i className="ri-check-line text-sm"></i>
                ) : (
                  <i className={`${step.icon} text-sm`}></i>
                )}
              </div>
              {!isLast && (
                <div className={`w-0.5 flex-1 min-h-[32px] mt-1 rounded-full transition-all duration-500 ${
                  isCompleted ? 'bg-green-300' : 'bg-gray-200'
                }`}></div>
              )}
            </div>

            {/* Right: content */}
            <div className={`pb-5 flex-1 min-w-0 ${isLast ? 'pb-0' : ''}`}>
              <div className="flex items-center justify-between gap-2">
                <div>
                  <p className={`text-sm font-bold leading-tight ${
                    isFuture ? 'text-gray-300' : isActive ? 'text-orange-600' : 'text-gray-800'
                  }`}>
                    {step.label}
                    {isActive && (
                      <span className="ml-2 text-[9px] bg-orange-100 text-orange-600 px-1.5 py-0.5 rounded-full font-black uppercase tracking-wide">
                        Current
                      </span>
                    )}
                  </p>
                  <p className={`text-xs mt-0.5 ${isFuture ? 'text-gray-200' : 'text-gray-400'}`}>
                    {step.sublabel}
                  </p>
                </div>
                {stepTime && (
                  <p className="text-[10px] text-gray-400 shrink-0 text-right">
                    {stepTime.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}
                    <br />
                    <span className="text-[9px]">
                      {stepTime.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                    </span>
                  </p>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
